"use client";
import { useEffect, useState } from "react";
import { Button, Col, Form, Modal, Row, Stack } from "react-bootstrap";
import "../../components/Calendar/Calendar.css";
import ModalIconTitle from "../../components/ModalIconTitle";
import { IoLocationOutline } from "react-icons/io5";
import { AxiosResponse } from "axios";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import TextInput from "../../components/Modal/Input/TextInput";
import { useModalWidthClass } from "../../hooks/useModalWidth";
import { BackendApi } from "../../utils/BackendApi";
import { ApiRoute } from "../../utils/ApiRoute";
import { cal_calendar, cal_place } from "../../class/prisma/db";
import type toast from "react-hot-toast";
import { SystemMsg } from "../../utils/SystemMsg";
import TextAreaInput from "./Input/TextAreaInput";
import {
  UpdatePlaceRequest,
  CreatePlaceRequest,
} from "../../class/place.schema";
import { MultiValue, SingleValue } from "react-select";
import TagSelector, { Tag, getSelectedOptionsById } from "../ReactSelect";
import MapModal from "./MapModal";
import { useI18n } from "../../i18n";

type PlaceModalProp = {
  toast: typeof toast;
  mode: string;
  show: boolean;
  onSaveBtnClick: (success: boolean, data: cal_place) => void;
  onClose: () => void;
  data: any;
};

type PlaceModalValidationResult = {
  name_exist: boolean;
  name_empty: boolean;
  calendar_id: boolean;
};

export default function PlaceModal({
  toast,
  mode,
  show,
  onSaveBtnClick,
  onClose,
  data,
}: PlaceModalProp) {
  const calendars = useSelector((state: RootState) => state.cache.calendars);
  const places = useSelector((state: RootState) => state.cache.places);
  const [id, setId] = useState(-1);
  const [calendarId, setCalendarId] = useState(-1);
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [showMapModal, setShowMapModal] = useState(false);

  const [calendarOptions, setCalendarOptions] = useState<Array<Tag>>([]);

  const [validationResult, setValidationResult] =
    useState<PlaceModalValidationResult>();

  const isAddMode = mode === "add";
  const isEditMode = mode === "edit";
  const { t } = useI18n();
  const modalTitle = mode === "add" ? t("place.add") : t("place.edit");

  const dialogClassName = useModalWidthClass();

  useEffect(() => {
    if (!show) return;
    if (isEditMode && !data) return;
    if (isEditMode) {
      setId(data.id ?? -1);
      setName(data.name ?? "");
      setAddress(data.address ?? "");
      setCalendarId(data.calendar_id ?? -1);
    }
  }, [mode, data, show]);

  useEffect(() => {
    if (Array.isArray(calendars)) {
      const options = [];
      for (const item of calendars as unknown as cal_calendar[]) {
        options.push({
          value: item.id.toString(),
          label: item.name,
          color: item.color ?? "#fff",
        });
      }
      setCalendarOptions(options);
    }
  }, [calendars]);

  const saveCalendarClick = async () => {
    if (!validateForm()) {
      return;
    }
    const requestBody: CreatePlaceRequest | UpdatePlaceRequest = {
      name,
      address,
      calendar_id: calendarId,
    };

    const callbackData = {
      id: id,
      calendar_id: calendarId,
      name: name,
      address: address,
    };

    const succFunc = (res: AxiosResponse) => {
      const success = res.status === 200;
      if (success) {
        toast.success(
          isAddMode
            ? SystemMsg.Calendar.createSuccess(name)
            : SystemMsg.Calendar.updateSuccess(name),
        );
      }
      if (typeof onSaveBtnClick === "function") {
        onSaveBtnClick(success, callbackData as cal_place);
      }
    };
    const errFunc = (res: AxiosResponse | string) => {
      toast.error(
        isAddMode
          ? SystemMsg.Place.createFailed
          : SystemMsg.Place.updateFailed(name),
      );
      if (typeof onSaveBtnClick === "function") {
        onSaveBtnClick(false, callbackData as cal_place);
      }
    };
    if (isEditMode) {
      await BackendApi.put(
        `${ApiRoute.place}/${id}`,
        requestBody,
        succFunc,
        errFunc,
      );
    }
    if (isAddMode) {
      await BackendApi.post(ApiRoute.place, requestBody, succFunc, errFunc);
    }
  };

  const onHide = () => {
    setId(-1);
    setName("");
    setAddress("");
    setCalendarId(-1);
    setValidationResult(undefined);
    if (typeof onClose === "function") {
      onClose();
    }
  };

  const validateForm = () => {
    const name_exist =
      isAddMode &&
      (places as unknown as cal_place[]).find(
        (x: cal_place) => x.name === name,
      ) !== undefined;

    const name_empty = !name || name.toString().length === 0;
    const name_valid = name_empty || name_exist ? false : true;

    const calendar_id_valid =
      !calendarId ||
      calendarId.toString().length === 0 ||
      !Number.isInteger(calendarId)
        ? false
        : true;

    const address_valid =
      !address || address.toString().length === 0 ? false : true;

    const result: PlaceModalValidationResult = {
      name_empty: name_empty,
      name_exist: name_exist,
      calendar_id: calendar_id_valid,
    };

    if (!name_valid) {
      if (name_exist) {
        toast.error(SystemMsg.Place.placeNameExists(name));
      } else {
        toast.error(SystemMsg.Place.placeNameInvalid(name));
      }
    }

    if (!address_valid) {
      toast.error(SystemMsg.Place.addressInvalid());
    }

    setValidationResult(result);
    const valid = name_valid && address_valid;
    return valid;
  };

  return (
    <>
      <MapModal
        placeName={name}
        address={address}
        show={showMapModal}
        onClose={() => {
          setShowMapModal(false);
        }}
      />
      <Modal
        show={show}
        onHide={onHide}
        backdrop="static"
        dialogClassName={dialogClassName}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <ModalIconTitle
              title={modalTitle}
              iconElement={<IoLocationOutline />}
            />
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="mb-3">
            <Form.Group as={Col} controlId="eventModal.placeId">
              <Form.Label>{t("fields.calendar")}</Form.Label>
              <TagSelector
                isMulti={false}
                options={calendarOptions as Tag[]}
                defaultValue={getSelectedOptionsById(
                  calendarOptions,
                  calendarId,
                )}
                onChange={(event: MultiValue<any> | SingleValue<any>) => {
                  if (!event || !Number.isInteger(Number(event.value))) {
                    setCalendarId(-1);
                    return;
                  }
                  setCalendarId(Number(event.value));
                }}
                placeholder={t("fields.selectCalendars")}
              />
            </Form.Group>
          </Row>
          <Row className="mb-3">
            <TextInput
              label={t("fields.name")}
              value={name}
              md={12}
              placeholder={t("fields.placeName")}
              setValueFunc={(val: string | number) => {
                setName(val as string);
              }}
              invalid={
                validationResult !== undefined &&
                validationResult?.name_empty === true
              }
            />
          </Row>
          <Row className="mb-3">
            <TextAreaInput
              label={t("fields.address")}
              value={address}
              placeholder={t("fields.address")}
              setValueFunc={(val: string | number) => {
                setAddress(val as string);
              }}
            />
          </Row>
        </Modal.Body>
        <Modal.Footer style={{ justifyContent: "flex-start" }}>
          <Button
            variant="outline-secondary"
            className="mr-5"
            onClick={() => {
              setShowMapModal(true);
            }}
          >
            {t("common.map")}
          </Button>
          <Stack
            direction="horizontal"
            style={{
              float: "right",
              display: "flex",
              width: "calc(95% - 50px)",
              justifyContent: "flex-end",
            }}
            gap={2}
          >
            <Button variant="primary" onClick={saveCalendarClick}>
              {t("common.saveChanges")}
            </Button>
            <Button variant="secondary" onClick={onHide}>
              {t("common.close")}
            </Button>
          </Stack>
        </Modal.Footer>
      </Modal>
    </>
  );
}
